import React, { useEffect, useState } from "react";
import { useLang } from "../contexts/LanguageContext";
import { ArrowRight, Sparkles } from "lucide-react";

export default function Hero({ onStart }) {
  const { isAr, t } = useLang();

  // Rotating hero tagline — alternates between two lines with a soft fade.
  // 4 s on screen per line, ~0.8 s crossfade. Loops forever while mounted.
  const taglines = [t("hero_tagline_1"), t("hero_tagline_2")];
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    // Reset to first line whenever language flips so both languages start in sync.
    setIdx(0);
    setVisible(true);
  }, [isAr]);
  useEffect(() => {
    const HOLD_MS = 4000;
    const FADE_MS = 800;
    let fadeTimer;
    const holdTimer = setInterval(() => {
      setVisible(false);
      fadeTimer = setTimeout(() => {
        setIdx((i) => (i + 1) % 2);
        setVisible(true);
      }, FADE_MS);
    }, HOLD_MS + FADE_MS);
    return () => { clearInterval(holdTimer); clearTimeout(fadeTimer); };
  }, [isAr]);

  // Open the floating Dana chat popover from anywhere via a global event.
  const openDana = () => window.dispatchEvent(new Event('tripex:open-dana'));

  const riyadhTokens = isAr
    ? ["كافد", "الدرعية", "البوليفارد", "سوق الزل"]
    : ["KAFD", "Diriyah", "Boulevard", "Souq Al-Zal"];
  const cityChips = [
    { ar: "الرياض · كافد", en: "Riyadh · KAFD" },
    { ar: "جدة · البلد", en: "Jeddah · Al-Balad" },
    { ar: "الشرقية · برج الخبر", en: "Eastern · Khobar Tower" },
    { ar: "العُلا · صخرة الفيل", en: "AlUla · Elephant Rock" },
    { ar: "عسير · المنتجعات", en: "Asir · Retreats" },
  ];

  return (
    <section id="top" className="relative pt-44 md:pt-52 pb-16 md:pb-20 overflow-hidden">
      <div className="relative z-10 mx-auto max-w-7xl px-5">
        <div className="max-w-4xl mx-auto text-center lg:text-start">
          {/* Text column */}
          <div data-testid="hero-text">
            {/* Tagline moved to Navbar — keeping breathing space here */}

            {/* Hero tagline — rotating carousel of two lines with a smooth
                0.8 s crossfade every 4 s. Colors + typography preserved from
                the original static hero so the look stays untouched. */}
            <style>{`
              .hero-tagline {
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
                gap: 0.15em;
              }
              .hero-tagline .line-main {
                font-size: clamp(2rem, 5vw, 4rem);
                font-weight: 900;
                color: #1a1a1a;
                line-height: 1.1;
              }
              .hero-tagline .line-amp {
                font-size: clamp(1.8rem, 4vw, 3.5rem);
                font-weight: 900;
                background: linear-gradient(to bottom, #4EC487, #E5DA97, #C499E8);
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
                background-clip: text;
                color: transparent;
                line-height: 1.2;
              }
              .hero-tagline .line-retreats {
                font-size: clamp(2rem, 5vw, 4rem);
                font-weight: 900;
                color: #51D5E0;
                line-height: 1.1;
              }
              .hero-tagline .line-for {
                font-size: clamp(0.9rem, 2vw, 1.4rem);
                font-weight: 500;
                color: #888888;
                line-height: 1.5;
              }
            `}</style>
            <h1
              className="mb-8 animate-fade-up"
              style={{
                animationDelay: '0.1s',
                minHeight: '22rem', // reserve enough height for both the stacked composition (tagline 1) and the testimonial (tagline 2) so the CTA row never jumps
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              data-testid={isAr ? "hero-tagline-ar" : "hero-tagline-en"}
              dir={isAr ? "rtl" : "ltr"}
            >
              <span
                data-testid="hero-tagline-rotating"
                style={{
                  display: 'inline-block',
                  transition: 'opacity 0.8s ease',
                  opacity: visible ? 1 : 0,
                  width: '100%',
                  textAlign: 'center',
                }}
              >
                {idx === 1 ? (
                  <span
                    style={{
                      fontFamily: isAr ? 'Cairo' : "'Montserrat', 'Plus Jakarta Sans', sans-serif",
                      fontWeight: isAr ? 700 : 800,
                      letterSpacing: isAr ? undefined : '-0.025em',
                      lineHeight: isAr ? 1.35 : 1.15,
                      fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
                      color: '#0f2421',
                      display: 'inline-block',
                    }}
                  >
                    <span aria-hidden style={{ color: '#0f2421' }}>&ldquo;</span>
                    {isAr ? (
                      <>
                        <span style={{ color: '#0f2421' }}>لم يكن مجرد مكان زرناه،</span>{' '}
                        <span style={{ color: '#51BDB0' }}>بل كانت تجربة عشناها</span>
                      </>
                    ) : (
                      <>
                        <span style={{ color: '#0f2421' }}>It wasn't just a place we visited,</span>{' '}
                        <span style={{ color: '#51BDB0' }}>it was an experience we lived</span>
                      </>
                    )}
                    <span style={{ color: '#E0B76E' }}>!</span>
                    <span aria-hidden style={{ color: '#0f2421' }}>&rdquo;</span>
                    <span
                      className="block mt-3 text-emerald-deep/60"
                      style={{
                        fontFamily: isAr ? 'Cairo' : "'Plus Jakarta Sans', sans-serif",
                        fontWeight: 500,
                        letterSpacing: '0.04em',
                        fontSize: '0.42em',
                      }}
                      data-testid="hero-tagline-attribution"
                    >
                      {isAr ? '— ضيف Tripex' : '— Tripex Guest'}
                    </span>
                  </span>
                ) : (
                  <span
                    className="hero-tagline"
                    style={{ fontFamily: isAr ? 'Cairo' : "'Montserrat', 'Plus Jakarta Sans', sans-serif" }}
                    data-testid="hero-tagline-stacked"
                  >
                    {isAr ? (
                      <>
                        <span className="line-main">تجارب ثقافية سعودية</span>
                        <span className="line-amp">و</span>
                        <span className="line-retreats">ريتريت Retreats</span>
                        <span className="line-for">لـ</span>
                        <span className="line-main">الشركات</span>
                        <span className="line-amp">و</span>
                        <span className="line-main">المجموعات</span>
                      </>
                    ) : (
                      <>
                        <span className="line-main">Saudi Cultural Experiences</span>
                        <span className="line-amp">&amp;</span>
                        <span className="line-retreats">Retreats</span>
                        <span className="line-for">for</span>
                        <span className="line-main">Companies</span>
                        <span className="line-amp">&amp;</span>
                        <span className="line-main">Groups</span>
                      </>
                    )}
                  </span>
                )}
              </span>
            </h1>

            {/* Riyadh tokens block removed per request */}

            <div className="flex flex-wrap items-center gap-3 animate-fade-up" style={{ animationDelay: '0.55s' }}>
              <button
                onClick={onStart}
                data-testid="hero-cta-primary"
                className="btn-primary"
                style={{ fontFamily: isAr ? 'Cairo' : 'Plus Jakarta Sans' }}
              >
                {t("hero_cta")}
                <ArrowRight size={18} className="flip-rtl" />
              </button>
              <button
                onClick={openDana}
                data-testid="hero-cta-dana"
                className="btn-dana-pill"
                aria-label={isAr ? "تحدث مع دانة - وكيل ذكاء صناعي شخصي" : "Chat with Dana - personal AI agent"}
              >
                <img
                  src="https://customer-assets.emergentagent.com/wingman/4d88747a-9285-4415-a858-393ef890ce1a/attachments/15204f80bb5a4f1087b4962c549d4d64_image.bin"
                  alt="Dana"
                  className="dana-avatar"
                />
                <span className="dana-label" dir={isAr ? 'rtl' : 'ltr'}>
                  <span className="main">{isAr ? "تحدث مع دانة" : "Chat with Dana"}</span>
                  <span className="sub">{isAr ? "وكيل ذكاء صناعي شخصي" : "personal AI agent"}</span>
                </span>
              </button>
            </div>
          </div>

          {/* Destinations visual card removed per request */}
        </div>
      </div>
    </section>
  );
}
